import {
    Box,
    Button,
    DropdownContent,
    DropdownMenu,
    DropdownTrigger,
    Heading,
    TextBlock,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { HSLColor, HuePicker } from "react-color";
import { useTranslation } from "react-i18next";

import styles from "./group-color-picker.scss";
import GroupIcon from "components/icons/GroupIcon";
import { resolveColor } from "utils/commonFunctions";
import { deriveGroupColors, getHue, getSelectedColor, GroupColors } from "utils/groupColors";

interface Props {
    backgroundColor: string;
    textColor: string;
    groupInitials?: string;
    onChange: (colors: GroupColors) => void;
}

const PLACEHOLDER_INITIALS = "AB";
const HUE_PICKER_HEIGHT = "16px";

interface PresetColorVariables {
    backgroundColorVariable: string;
    textColorVariable: string;
}

const PRESET_COLOR_VARIABLES: PresetColorVariables[] = [
    { backgroundColorVariable: "--color-red-400", textColorVariable: "--color-red-900" },
    { backgroundColorVariable: "--color-orange-300", textColorVariable: "--color-orange-950" },
    { backgroundColorVariable: "--color-orange-300", textColorVariable: "--color-orange-1000" },
    {
        backgroundColorVariable: "--app-groupIcon-background-orange",
        textColorVariable: "--app-groupIcon-text-orange",
    },
    { backgroundColorVariable: "--color-yellow-200", textColorVariable: "--color-yellow-900" },
    { backgroundColorVariable: "--color-green-300", textColorVariable: "--color-green-900" },
    {
        backgroundColorVariable: "--app-groupIcon-background-green",
        textColorVariable: "--app-groupIcon-text-green",
    },
    { backgroundColorVariable: "--color-cyan-400", textColorVariable: "--color-cyan-900" },
    { backgroundColorVariable: "--color-sky-300", textColorVariable: "--color-sky-900" },
    {
        backgroundColorVariable: "--app-groupIcon-background-blue",
        textColorVariable: "--app-groupIcon-text-blue",
    },
    { backgroundColorVariable: "--color-purple-400", textColorVariable: "--color-purple-950" },
    {
        backgroundColorVariable: "--app-groupIcon-background-purple",
        textColorVariable: "--app-groupIcon-text-purple",
    },
    { backgroundColorVariable: "--color-pink-200", textColorVariable: "--color-pink-900" },
    { backgroundColorVariable: "--color-pink-200", textColorVariable: "--color-pink-1000" },
    { backgroundColorVariable: "--color-grey-300", textColorVariable: "--color-grey-900" },
];

function resolvePresetColors(variables: PresetColorVariables): GroupColors {
    return {
        backgroundColor: resolveColor(variables.backgroundColorVariable),
        textColor: resolveColor(variables.textColorVariable),
    };
}

function toSwatchBackground(variables: PresetColorVariables) {
    return `linear-gradient(90deg, var(${variables.backgroundColorVariable}) 50%, var(${variables.textColorVariable}) 50%)`;
}

function isSameColors(first: GroupColors, second: GroupColors) {
    return (
        first.backgroundColor.toLowerCase() === second.backgroundColor.toLowerCase() &&
        first.textColor.toLowerCase() === second.textColor.toLowerCase()
    );
}

const HuePointer = (props: { hsl: HSLColor }) => (
    <div className={styles.huePointer} style={{ background: `hsl(${props.hsl.h}, 100%, 50%)` }} />
);

const GroupColorPicker = (props: Props) => {
    const { t } = useTranslation();
    const [isCustomColorOpen, setIsCustomColorOpen] = React.useState(false);
    const [hue, setHue] = React.useState(getHue(props.backgroundColor));

    const selectedColor = getSelectedColor(hue);
    const previewColors = deriveGroupColors(selectedColor);
    const currentColors = { backgroundColor: props.backgroundColor, textColor: props.textColor };
    const presets = PRESET_COLOR_VARIABLES.map((variables) => ({
        variables,
        colors: resolvePresetColors(variables),
    }));
    const isCustomColorSelected = !presets.some((preset) => isSameColors(preset.colors, currentColors));

    const handleApply = () => {
        props.onChange(previewColors);
        setIsCustomColorOpen(false);
    };

    return (
        <div className={styles.swatches}>
            {presets.map((preset, i) => (
                <button
                    key={i}
                    type="button"
                    className={classNames(styles.swatch, {
                        [styles.selected]: isSameColors(preset.colors, currentColors),
                    })}
                    style={{ background: toSwatchBackground(preset.variables) }}
                    onClick={() => props.onChange(preset.colors)}
                />
            ))}
            <DropdownMenu
                position="bottom-start"
                dropdownOffset="Xs"
                isDropdownOpen={isCustomColorOpen}
                onDropdownOpenChange={setIsCustomColorOpen}
            >
                <DropdownTrigger>
                    <Tooltip>
                        <TooltipContent>{t("UserGroups.manageUserGroupsView.customColor")}</TooltipContent>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className={classNames(styles.swatch, styles.customColorSwatch, {
                                    [styles.selected]: isCustomColorSelected,
                                })}
                            />
                        </TooltipTrigger>
                    </Tooltip>
                </DropdownTrigger>
                <DropdownContent className={styles.groupColorPicker}>
                    <Box columnLayout gap="Md" padding="Md">
                        <Heading variant="SUBTITLE_2" underline disableBottomSpacing>
                            {t("UserGroups.manageUserGroupsView.selectCustomColor")}
                        </Heading>

                        <GroupIcon
                            backgroundColor={previewColors.backgroundColor}
                            textColor={previewColors.textColor}
                            groupInitials={props.groupInitials ?? PLACEHOLDER_INITIALS}
                        />
                        <Box columnLayout gap="Xxxs" className={styles.hueSection}>
                            <TextBlock disableBottomSpacing>
                                {t("UserGroups.manageUserGroupsView.backgroundColor")}
                            </TextBlock>
                            <HuePicker
                                color={selectedColor}
                                width="100%"
                                height={HUE_PICKER_HEIGHT}
                                pointer={HuePointer}
                                onChange={(color) => setHue(Math.round(color.hsl.h))}
                            />
                        </Box>
                        <Box horizontalAlignment="Right" gap="Sm" paddingTop={"Xxs"}>
                            <Button variant="Primary" small onClick={handleApply}>
                                {t("Common.apply")}
                            </Button>
                            <Button variant="Secondary" small onClick={() => setIsCustomColorOpen(false)}>
                                {t("Common.cancel")}
                            </Button>
                        </Box>
                    </Box>
                </DropdownContent>
            </DropdownMenu>
        </div>
    );
};

export default GroupColorPicker;
